import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

export type Language = 'tr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language') as Language;
    return saved || 'tr';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    i18n.changeLanguage(lang);
    
    // Update document lang attribute for SEO
    document.documentElement.lang = lang;
    
    // ✨ Tüm API verilerini yeniden yükle (dil değiştiği için)
    queryClient.invalidateQueries();
    
    // Update subdomain if needed (for future use)
    // window.location.hostname = `${lang}.companycrv.com`
  };

  const toggleLanguage = () => {
    const newLang: Language = language === 'tr' ? 'en' : 'tr';
    setLanguage(newLang);
  };

  useEffect(() => {
    // Initialize i18n language on mount
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

